import { forwardRef, useState, type ImgHTMLAttributes, type SyntheticEvent } from "react";
import { buildSrcSet, isTransformable, optimizeImageUrl } from "@/utils/imageTransform";

/**
 * Emits a <picture> with AVIF + WebP <source> tags and a fallback <img>.
 *
 * The Supabase image transformer supports explicit `format=avif` and
 * `format=webp`. By declaring both source types with matching `srcSet`s the
 * browser picks the first MIME type it can decode: AVIF (~30% smaller than
 * WebP) on modern browsers, WebP everywhere else, and the origin-format <img>
 * as the ultimate fallback.
 *
 * For non-Supabase URLs (local imports, external CDNs) the transformer can't
 * re-encode the file, so we just render a plain <img>.
 *
 * Opt-in `withSkeleton` wraps the picture in a relative span and layers a
 * shimmer overlay underneath, cross-fading out on load, matches LazyImage's
 * treatment so lightbox/QuickView image areas stay visually consistent while
 * they decode.
 */
export interface PictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> {
 src: string;
 /** Widths (px) used to build responsive srcsets. */
 widths?: number[];
 /** Rendered width the browser should target. Also drives the fallback `src`. */
 displayWidth?: number;
 /** JPEG-equivalent quality (transformer clamps this per-format). */
 quality?: number;
 /** `sizes` attribute passed to every <source> and the fallback <img>. */
 sizes?: string;
 /** Extra classes on the outer <picture>. */
 pictureClassName?: string;
 /** Wrap the <picture> in a relative span with a shimmer skeleton overlay
 * that fades out once the image loads. Matches `LazyImage`'s treatment. */
 withSkeleton?: boolean;
 /** Extra classes on the skeleton wrapper span (only used with `withSkeleton`). */
 wrapperClassName?: string;
}

const Picture = forwardRef<HTMLImageElement, PictureProps>(function Picture(
 {
 src,
 widths = [400, 600, 900, 1200, 1600],
 displayWidth = 900,
 quality = 82,
 sizes,
 pictureClassName,
 alt = "",
 withSkeleton = false,
 wrapperClassName = "",
 onLoad,
 ...imgProps
 },
 ref,
) {
 const canTransform = isTransformable(src);
 const [loaded, setLoaded] = useState(false);

 const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
 setLoaded(true);
 onLoad?.(e);
 };

 const skeleton = withSkeleton ? (
 <span
 aria-hidden
 className={`absolute inset-0 bg-[linear-gradient(110deg, hsl(var(--muted))_20%, hsl(var(--muted-foreground)/0.08)_45%, hsl(var(--muted))_70%)] bg-[length:200%_100%] animate-shimmer transition-opacity duration-500 pointer-events-none ${
 loaded ? "opacity-0" : "opacity-100"
 }`}
 />
 ) : null;

 // Non-Supabase asset: transformer can't help, just render a plain <img>.
 if (!canTransform) {
 const bareImg = (
 <img ref={ref} src={src} alt={alt} sizes={sizes} onLoad={handleLoad} {...imgProps} />
 );
 if (!withSkeleton) return bareImg;
 return (
 <span className={`relative block overflow-hidden ${wrapperClassName}`}>
 {skeleton}
 {bareImg}
 </span>
 );
 }

 const avifSet = buildSrcSet(src, widths, quality, "avif");
 const webpSet = buildSrcSet(src, widths, quality, "webp");
 const fallbackSrc = optimizeImageUrl(src, { width: displayWidth, quality });
 const fallbackSet = buildSrcSet(src, widths, quality);

 const picture = (
 <picture className={pictureClassName}>
 {avifSet && <source type="image/avif" srcSet={avifSet} sizes={sizes} />}
 {webpSet && <source type="image/webp" srcSet={webpSet} sizes={sizes} />}
 <img
 ref={ref}
 src={fallbackSrc}
 srcSet={fallbackSet || undefined}
 sizes={sizes}
 alt={alt}
 onLoad={handleLoad}
 {...imgProps}
 />
 </picture>
 );

 if (!withSkeleton) return picture;

 return (
 <span className={`relative block overflow-hidden ${wrapperClassName}`}>
 {skeleton}
 {picture}
 </span>
 );
});

export default Picture;
